import React, {createContext, useContext, useEffect, useState} from "react";
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useMediaQuery,
    useTheme,
    Button,
    Box,
    Divider,
    Alert,
    ToggleButtonGroup,
    ToggleButton, Typography
} from "@mui/material";
import {subscriptionServices} from "../../../service/subscriptionService";
import {CreditCard, Add, Edit} from "@mui/icons-material";
import UpdatePaymentMethod from "./UpdatePaymentMethod";
import Spinner from "../../spinner/Spinner";
import {useElements, useStripe} from "@stripe/react-stripe-js";
import {businessSubscriptionPlans, countries, individualSubscriptionPlans} from "../../utils/DataList";
import {useForm} from "react-hook-form";
import {BillingDetailsFields} from "./UpdateBillingDetails";
import CustomSnackbar from "../../alert/CustomSnackbar";
import {SubscriptionDialog as Dialog, subscriptionInset} from "./SubscriptionDesign";
import {getSettingsColors} from "../SettingsDesign";

import PlanCard from "./PlanCard";

export const ChangePlanContext = createContext(null);

function ChangePlan(props) {
    const [step, setStep] = useState(1);
    const [planName, setPlanName] = useState("");
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);
    const [billingDetails, setBillingDetails] = useState(null);

    useEffect(() => {
        async function fetchData() {
            await subscriptionServices.fetchSubscription().then((result) => {
                setDetails(result);
            }).catch(() => {
            }).finally(() => {
                setLoading(false);
            });
        }

        if (details === null && props?.open) {
            fetchData();
        }
    }, [details, props.open]);

    return (
        <ChangePlanContext.Provider
            value={{
                step, setStep,
                planName, setPlanName,
                loading, setLoading,
                details, setDetails,
                billingDetails, setBillingDetails
            }}>
            {step === 1 && <Step1ChoosePlan {...props}/>}
            {step === 2 && <Step2UpdateBillingDetails {...props}/>}
            {step === 3 && <Step3ConfirmPayment {...props}/>}
        </ChangePlanContext.Provider>
    );
}

const Step1ChoosePlan = (props) => {
    const {setStep, setPlanName, loading, details, setDetails} = useContext(ChangePlanContext);
    const {onClose, open} = props;

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [planType, setPlanType] = useState("individual");

    const handleChoosePlan = (planName) => {

        if (planName === details?.plan?.name) {
            return;
        }

        individualSubscriptionPlans.forEach((plan) => {
            if (planName === plan?.name) {
                setPlanName(plan?.name);
                setStep(2);
            }
        });

        businessSubscriptionPlans.forEach((plan) => {
            if (planName === plan?.name) {
                setPlanName(plan?.name);
                setStep(2);
            }
        });
    };

    const handleOnClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        setDetails(null);
        onClose();
    };

    const handleToggleButtonChange = (event, newPlanType) => {
        if (newPlanType === null) {
            return false;
        }
        setPlanType(newPlanType);
    };

    return (
        <Dialog
            open={open}
            onClose={handleOnClose}
            fullWidth={true}
            fullScreen={fullScreen}
            aria-labelledby="step1-choose-subscription-plan"
            maxWidth={details?.plan?.name !== 'Free' || planType === 'business' ? 'md' : 'lg'}
        >
            {loading && <Spinner size={32} overComponentBox={true}/>}

            <DialogTitle id="step1-choose-subscription-plan">
                Change subscription plan
                <Typography component="p" sx={{fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.75}}>Step 1
                    of 3 · Choose the plan that fits your workspace.</Typography>
            </DialogTitle>

            {!loading && <DialogContent>

                <Box textAlign="center" pb={2}>
                    <ToggleButtonGroup
                        aria-label="Plan category"
                        color="primary"
                        value={planType}
                        exclusive
                        onChange={handleToggleButtonChange}
                        sx={{
                            p: 0.5, gap: 0.5, bgcolor: theme => getSettingsColors(theme).inset, borderRadius: '10px',
                            '& .MuiToggleButtonGroup-grouped': {border: 0, borderRadius: '7px', minHeight: 35, px: 2, py: 0.75, fontSize: 13, textTransform: 'none'}
                        }}
                    >
                        <ToggleButton value="individual">Individual</ToggleButton>
                        <ToggleButton value="business">Business</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {planType === "individual" &&
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: details?.plan?.name === 'Free' ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                        {individualSubscriptionPlans?.map((plan, i) => {

                            if (details?.plan?.name !== "Free" && plan?.name === "Free") {
                                return false;
                            }

                            return (
                                <Box sx={{minWidth: 0}} key={i}>
                                    <PlanCard
                                        planName={plan?.name}
                                        price={plan?.price}
                                        events={plan?.events}
                                        overage={plan?.overagePrice}
                                        customDomains={plan?.customDomains}
                                        months={plan?.dataRetentionInMonths}
                                        totalNumberOfRecords={plan?.totalNumberOfRecords}
                                        users={plan?.users}
                                        choosePlan={handleChoosePlan}
                                        currentPlanName={details?.plan?.name}
                                        integrations={plan?.integrations}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                }

                {planType === "business" &&
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                        {businessSubscriptionPlans?.map((plan, i) => {
                            return (
                                <Box sx={{minWidth: 0}} key={i}>
                                    <PlanCard
                                        planName={plan?.name}
                                        price={plan?.price}
                                        events={plan?.events}
                                        overage={plan?.overagePrice}
                                        customDomains={plan?.customDomains}
                                        months={plan?.dataRetentionInMonths}
                                        totalNumberOfRecords={plan?.totalNumberOfRecords}
                                        users={plan?.users}
                                        choosePlan={handleChoosePlan}
                                        currentPlanName={details?.plan?.name}
                                        integrations={plan?.integrations}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                }

                <Box sx={{paddingTop: 1, textAlign: "center", fontSize: 12, color: "text.disabledLight"}}>
                    * An event should be defined as every visit / click / conversion.
                </Box>
                <Box sx={{paddingTop: 1, textAlign: "center", fontSize: 12, color: "text.disabledLight"}}>
                    * Overage charge occurs when the plan’s included events limit is exceeded.
                </Box>
                <Box sx={{paddingTop: 1, textAlign: "center", fontSize: 12, color: "text.disabledLight"}}>
                    * All prices exclude VAT. Applicable VAT may be added at checkout based on your billing country.
                </Box>
            </DialogContent>}
            <DialogActions sx={{padding: "15px"}}>
                <Button type="button" color="inherit" variant="text" onClick={handleOnClose}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};


function Step2UpdateBillingDetails(props) {
    const {setStep, setBillingDetails} = useContext(ChangePlanContext);
    const {onClose, open} = props;

    const {register, handleSubmit, control, setValue, formState: {errors}} = useForm();

    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleOnSubmit = async (data) => {
        setLoading(true);
        setErrorMsg("");

        let dataObj = {
            name: data?.name,
            address1: data?.address1,
            address2: data?.address2,
            country: data?.country?.code,
            city: data?.city,
            state: data?.state,
            zip: data?.zip,
            vatNumber: data?.vatNumber,
        };

        setBillingDetails(dataObj);

        await subscriptionServices.updateBillingDetails(dataObj).then(() => {
            //handleClose();
            setStep(3);
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        setErrorMsg("");
        onClose();
    };

    const handlePreviousStep = () => {
        setStep(1);
    };

    useEffect(() => {
        if (open) {
            setLoading(true);
            setErrorMsg("");
            subscriptionServices.getBillingDetails().then((result) => {
                setValue("name", result?.name);
                setValue("address1", result?.address1);
                setValue("address2", result?.address2);
                setValue("city", result?.city);
                setValue("state", result?.state);
                setValue("zip", result?.zip);
                setValue("vatNumber", result?.vatNumber);
                countries.forEach((item) => {
                    if (item.code === result?.country && item.code !== "") {
                        setValue("country", {code: item.code, label: item.label});
                    }
                });
            }).catch((error) => {
                setErrorMsg(error?.response?.data?.error);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [open, control, setValue]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth={true}
            fullScreen={fullScreen}
            aria-labelledby="update-billing-details"
        >
            <DialogTitle id="update-billing-details">Update billing details
                <Typography component="p" sx={{fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.75}}>Step 2
                    of 3 · Confirm the billing information for your plan.</Typography>
            </DialogTitle>
            {loading && <Spinner size={32} overComponentBox={true}/>}
            <DialogContent sx={{padding: "6px"}}>
                <form onSubmit={handleSubmit(handleOnSubmit)} autoComplete={"off"}>
                    {errorMsg !== "" &&
                        <Alert severity="error" variant="filled" sx={{mb: 2}}>{errorMsg}</Alert>
                    }
                    <BillingDetailsFields register={register} control={control} errors={errors} disabled={loading}/>
                </form>
            </DialogContent>
            <DialogActions sx={{padding: 2}}>
                <Button type="button" color="inherit" variant="text" onClick={handlePreviousStep} disabled={loading}>
                    Back
                </Button>
                <Button type="submit" color="primary" variant="contained" onClick={handleSubmit(handleOnSubmit)}
                        disabled={loading}>
                    Next
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const Step3ConfirmPayment = (props) => {
    const {setStep, planName, setDetails, loading, setLoading, billingDetails} = useContext(ChangePlanContext);
    const {onClose, open} = props;

    const [paymentMethod, setPaymentMethod] = useState(null);
    const [openUpdatePaymentMethod, setOpenUpdatePaymentMethod] = useState(false);
    const [reloadPaymentMethod, setReloadPaymentMethod] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [upcomingInvoice, setUpcomingInvoice] = useState(null);
    const [couponResult, setCouponResult] = useState(null);
    const [coupon, setCoupon] = useState("");
    const [renewalPrice, setRenewalPrice] = useState(0.00);
    const [discount, setDiscount] = useState(0.00);
    const [planPrice, setPlanPrice] = useState(0.00);
    const [totalDueNow, setTotalDueNow] = useState(0.00);
    const [vatAmount, setVatAmount] = useState(0.00);
    const [subTotalAmount, setSubTotalAmount] = useState(0.00);
    const [totalAmount, setTotalAmount] = useState(0.00);
    const [vatDisplayName, setVatDisplayName] = useState("");
    const [discountCouponName, setDiscountCouponName] = useState("");
    const [discountPercent, setDiscountPercent] = useState(0);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const stripe = useStripe();
    const elements = useElements();

    const handleConfirmPayment = () => {
        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setErrorMsg("");

        subscriptionServices.update(planName, discountCouponName).then((result) => {
            if (result?.status === "active") {
                onClose();
                setStep(1);
                setDetails(null);
            } else if (result?.paymentIntentStatus === "requires_action" || result?.paymentIntentStatus === "requires_payment_method") {
                stripe.confirmCardPayment(result?.paymentIntentClientSecret, {payment_method: result?.paymentMethodId})
                    .then((result) => {
                        if (result?.error) {
                            setErrorMsg(result?.error?.message);
                        } else {
                            if (result.paymentIntent.status === 'succeeded') {
                                // There's a risk of the customer closing the window before callback
                                // execution. To handle this case, set up a webhook endpoint and
                                // listen to invoice.paid. This webhook endpoint returns an Invoice.
                                subscriptionServices.updateSubscriptionStatus().then(() => {
                                    onClose();
                                    setStep(1);
                                    setDetails(null);
                                }).catch((error) => {
                                    setErrorMsg(error?.response?.data?.error);
                                });
                            }
                        }
                    });
            }
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handlePreviousStep = () => {
        setLoading(true);
        setDetails(null);
        setStep(2);
    };

    const handleClickOpenUpdatePaymentMethod = () => {
        setOpenUpdatePaymentMethod(true);
    };

    const handleCloseUpdatePaymentMethod = () => {
        setOpenUpdatePaymentMethod(false);
    };

    const handleReloadPaymentMethod = () => {
        setReloadPaymentMethod(true);
    };

    const handleApplyCoupon = async () => {
        setErrorMsg("");

        if (coupon === "") {
            setErrorMsg("Please enter a valid coupon code.")
            return false;
        }

        setLoading(true);
        await subscriptionServices.applyCouponToSubscription(coupon).then((result) => {
            setCouponResult(result);
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
            setLoading(false);
        });
    };

    useEffect(() => {
        async function fetchData(planName) {
            setLoading(true);
            await subscriptionServices.retrieveUpcomingInvoice(planName).then((result) => {
                setUpcomingInvoice(result);
            }).catch((error) => {
                setErrorMsg(error?.response?.data?.error);
                setLoading(false);
            }).finally(() => {
                setLoading(false);
            });
        }

        fetchData(planName);
    }, [planName, setLoading, couponResult]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            await subscriptionServices.getPaymentMethod().then((result) => {
                setPaymentMethod(result);
            }).finally(() => {
                setLoading(false);
                setReloadPaymentMethod(false);
            });
        }

        if (reloadPaymentMethod) {
            fetchData();
        }
    }, [reloadPaymentMethod, setLoading]);

    useEffect(() => {
        if (planName !== "" && planName !== undefined) {
            individualSubscriptionPlans.forEach((plan) => {
                if (planName === plan?.name) {
                    setPlanPrice(plan?.price);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planName]);

    useEffect(() => {
        if (planName !== "" && planName !== undefined) {
            businessSubscriptionPlans.forEach((plan) => {
                if (planName === plan?.name) {
                    setPlanPrice(plan?.price);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planName]);

    useEffect(() => {
        if (upcomingInvoice !== null) {
            setTotalDueNow(upcomingInvoice?.amountDue / 100);

            if (upcomingInvoice?.in?.default_tax_rates?.length === 1) {
                setVatAmount(upcomingInvoice?.vatAmount);
                if (upcomingInvoice?.vatAmount === 0) {
                    setVatDisplayName("0%");
                } else {
                    setVatDisplayName(" - " + billingDetails?.country + " " + upcomingInvoice?.vatRate + "%");
                }
            } else {

            }

            setSubTotalAmount(upcomingInvoice?.subTotalAmount);
            setTotalAmount(upcomingInvoice?.totalAmount);

            if (upcomingInvoice?.appliedDiscount?.name !== "") {
                let nextPlanPrice = upcomingInvoice?.totalAmount;
                let off = 0.00;
                if (upcomingInvoice?.appliedDiscount?.percentOff !== 0) {
                    off = nextPlanPrice * (upcomingInvoice?.appliedDiscount?.percentOff / 100);
                }
                setDiscountCouponName(upcomingInvoice?.appliedDiscount?.name);
                setDiscountPercent(upcomingInvoice?.appliedDiscount?.percentOff);
                setDiscount(off);
                setRenewalPrice(nextPlanPrice - off);
            }
        }
    }, [discount, planName, planPrice, upcomingInvoice, billingDetails?.country]);

    useEffect(() => {
        if (errorMsg !== "") {
            setTimeout(() => {
                setErrorMsg("");
            }, 3000);
        }
    }, [errorMsg])

    return (
        <Dialog open={open}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') {
                        return false;
                    }
                    onClose();
                }}
                fullWidth={true}
                fullScreen={fullScreen}
                aria-labelledby="selected-plan"
                maxWidth="sm"
        >
            {loading && <Spinner size={32} overComponentBox={true}/>}
            <DialogTitle id="selected-plan">Change plan to {planName}
                <Typography component="p" sx={{fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.75}}>Step 3
                    of 3 · Review your charges and payment method.</Typography>
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" sx={{mb: 2}}>
                    The payment amount will be charged automatically each month.
                    Subsequent payment is scheduled upon renewal on {upcomingInvoice?.nextPaymentDate}
                </Alert>
                <Box sx={subscriptionInset}>
                    <Box display="flex" flexDirection="row" sx={{fontWeight: 400}}>
                        <Box flexGrow={1} p={1}>
                            Subscribing to plan
                        </Box>
                        <Box p={1}>{planName}</Box>
                    </Box>
                    <Box display="flex" flexDirection="row" sx={{fontWeight: 400}}>
                        <Box flexGrow={1} p={1}>
                            Current balance:
                        </Box>
                        <Box
                            p={1}>€ {((upcomingInvoice?.currentBalance ? upcomingInvoice?.currentBalance : 0) / 100).toFixed(2)}</Box>
                    </Box>
                    <Box display="flex" flexDirection="row" sx={{fontWeight: 400}}>
                        <Box flexGrow={1} p={1}>
                            Plan price
                        </Box>
                        <Box p={1}>€ {subTotalAmount.toFixed(2)}</Box>
                    </Box>
                    <Box display="flex" flexDirection="row" sx={{fontWeight: 400}}>
                        <Box flexGrow={1} p={1}>
                            VAT {vatDisplayName}
                        </Box>
                        <Box p={1}>€ {vatAmount.toFixed(2)}</Box>
                    </Box>
                    <Box display="flex" flexDirection="row" sx={{fontWeight: 400}}>
                        <Box flexGrow={1} p={1}>
                            Total
                        </Box>
                        <Box p={1}>€ {totalAmount.toFixed(2)}</Box>
                    </Box>
                    {discount > 0 &&
                        <>
                            <Box display="flex" flexDirection="row" sx={{color: "primary.main", fontWeight: 600}}>
                                <Box flexGrow={1} p={1}>
                                    Discount - {discountCouponName} - [{discountPercent}% off]
                                </Box>
                                <Box p={1}>- € {discount.toFixed(2)}</Box>
                            </Box>
                            <Divider/>
                            <Box display="flex" flexDirection="row" sx={{fontWeight: 600}}>
                                <Box flexGrow={1} p={1}>
                                    Total cost on renewal (should be paid on {upcomingInvoice?.nextPaymentDate})
                                </Box>
                                <Box p={1}>€ {renewalPrice.toFixed(2)}</Box>
                            </Box>
                        </>
                    }

                    <Divider/>
                    <Box display="flex" flexDirection="row" sx={{fontWeight: 600}}>
                        <Box flexGrow={1} p={1}>
                            Total due now (prorated charges)
                        </Box>
                        <Box p={1}>€ {totalDueNow.toFixed(2)}</Box>
                    </Box>
                </Box>
                {planName !== "Free" &&
                    <Box display="flex" flexDirection="column" sx={{mt: 3, mb: 1}}>
                        <Box sx={{fontWeight: 600, paddingLeft: "10px", fontSize: "14px"}}>Coupon code</Box>
                        <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                            <Box flexGrow={1}>
                                <TextField
                                    variant="outlined"
                                    margin="dense"
                                    name="coupon"
                                    type="text"
                                    size="small"
                                    onChange={(e) => setCoupon(e.target.value)}
                                    placeholder="Enter coupon code"
                                    fullWidth={true}
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box ml={1} sx={{paddingTop: "4px"}}>
                                <Button
                                    type="button"
                                    color="primary"
                                    variant="contained"
                                    onClick={handleApplyCoupon}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                }

                {couponResult?.valid &&
                    <CustomSnackbar severity={"success"} message={"Coupon code successfully applied!"} timeout={2500}/>
                }

                <Box sx={{...subscriptionInset, mt: 3}}>
                    <Typography sx={{fontSize: 14, mb: 2, fontWeight: 600}}>Payment method</Typography>

                    {paymentMethod?.brand === "" && loading === false &&
                        <Box sx={{textAlign: "center", padding: 2}}>
                            <Button type="button" variant="text" color="primary" sx={{textTransform: "none"}}
                                    onClick={handleClickOpenUpdatePaymentMethod}>
                                <Add/> Add payment method
                            </Button>
                            <UpdatePaymentMethod open={openUpdatePaymentMethod}
                                                 onClose={handleCloseUpdatePaymentMethod}
                                                 reloadPaymentMethod={handleReloadPaymentMethod}/>
                        </Box>
                    }

                    {paymentMethod?.brand !== "" && loading === false &&
                        <Box>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, fontSize: 14}}>
                                <CreditCard sx={{fontSize: 22}}/>
                                <Box flexGrow={1} sx={{
                                    "&::first-letter": {
                                        textTransform: "capitalize"
                                    }
                                }}>
                                    {paymentMethod?.brand} •••• {paymentMethod?.last4}
                                </Box>
                                <Box sx={{color: 'text.secondary', fontSize: 13}}>
                                    Expires {paymentMethod?.expMonth}/{paymentMethod?.expYear}
                                </Box>
                            </Box>
                            <Box sx={{textAlign: "center", padding: 2}}>
                                <Button type="button" variant="text" color="primary" sx={{textTransform: "none"}}
                                        onClick={handleClickOpenUpdatePaymentMethod}>
                                    <Edit sx={{marginRight: "6px", fontSize: "18px"}}/> Update payment method
                                </Button>
                                <UpdatePaymentMethod open={openUpdatePaymentMethod}
                                                     onClose={handleCloseUpdatePaymentMethod}
                                                     reloadPaymentMethod={handleReloadPaymentMethod}/>
                            </Box>
                        </Box>
                    }

                </Box>

                {errorMsg !== "" &&
                    <CustomSnackbar severity={"error"} message={errorMsg} timeout={2500}/>
                }

            </DialogContent>
            <DialogActions sx={{padding: 2}}>
                <Button type="button" color="inherit" variant="text" onClick={handlePreviousStep} disabled={loading}>
                    Back
                </Button>
                <Button
                    type="button"
                    disabled={loading || paymentMethod === null || paymentMethod?.brand === ""}
                    color="primary"
                    variant="contained"
                    onClick={handleConfirmPayment}
                >
                    Confirm Payment
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePlan;
